'use client';

import { c as _c } from "react/compiler-runtime";
import './tooltip.scss';
import * as RdxTooltip from '@radix-ui/react-tooltip';
import React, { createContext, useContext, useRef, useState } from 'react';
import { cn, isOnDesktop } from '../../utils/helpers.ts';
const enableClicks = !isOnDesktop();

/**
 * Tooltip Global Provider.
 */
interface TooltipProviderProps extends React.PropsWithChildren {
  delayDuration?: RdxTooltip.TooltipProviderProps['delayDuration'];
  skipDelayDuration?: RdxTooltip.TooltipProviderProps['skipDelayDuration'];
  disableHoverableContent?: RdxTooltip.TooltipProviderProps['disableHoverableContent'];
}
export const TooltipProvider: React.FC<TooltipProviderProps> = t0 => {
  const $ = _c(5);
  const {
    children,
    delayDuration: t1,
    disableHoverableContent,
    skipDelayDuration
  } = t0;
  const delayDuration = t1 === undefined ? 0 : t1;
  const t2 = enableClicks ? 0 : delayDuration;
  let t3;
  if ($[0] !== children || $[1] !== disableHoverableContent || $[2] !== skipDelayDuration || $[3] !== t2) {
    t3 = <RdxTooltip.Provider delayDuration={t2} skipDelayDuration={skipDelayDuration} disableHoverableContent={disableHoverableContent}>{children}</RdxTooltip.Provider>;
    $[0] = children;
    $[1] = disableHoverableContent;
    $[2] = skipDelayDuration;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

/**
 * Trigger Trigger Context
 */
interface TooltipTriggerContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  suppressDesktopClose: () => void;
}
const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
  isOpen: false,
  setIsOpen: () => {},
  triggerRef: {
    current: null
  },
  suppressDesktopClose: () => {}
});
const ToolTipTriggerProvider = TooltipTriggerContext.Provider;

/**
 * Tooltip
 */
export interface TooltipProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Sets the initial open state for uncontrolled tooltips.
   * After mount, the tooltip manages its own state and can still be opened or closed by user interaction.
   */
  isDefaultOpen?: RdxTooltip.TooltipProps['defaultOpen'];
  /**
   * Controls the open state of the tooltip.
   * When provided, the parent owns the state and must update `isOpen` in response to `onOpenChange`.
   */
  isOpen?: RdxTooltip.TooltipProps['open'];
  /**
   * Called whenever the tooltip requests an open-state change.
   * Use this with `isOpen` for controlled tooltips instead of relying on trigger click events alone.
   */
  onOpenChange?: RdxTooltip.TooltipProps['onOpenChange'];
}

/**
 * Supports both uncontrolled and controlled usage.
 * Use `isDefaultOpen` when the tooltip should start open but remain dismissible.
 * Use `isOpen` with `onOpenChange` when the parent should fully own the tooltip state.
 */
export const Tooltip = t0 => {
  const $ = _c(22);
  let children;
  let isOpen;
  let onOpenChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      isDefaultOpen: t1,
      isOpen,
      onOpenChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = isOpen;
    $[3] = onOpenChange;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    isOpen = $[2];
    onOpenChange = $[3];
    props = $[4];
    t1 = $[5];
  }
  const isDefaultOpen = t1 === undefined ? undefined : t1;
  const isControlled = isOpen !== undefined;
  const triggerRef = useRef(null);
  const suppressDesktopCloseRef = useRef(false);
  const suppressDesktopCloseTimerRef = useRef(null);
  const [uncontrolledOpenState, setUncontrolledOpenState] = useState(isDefaultOpen ?? false);
  const openState = isControlled ? isOpen : uncontrolledOpenState;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (enableClicks) {
        return;
      }
      suppressDesktopCloseRef.current = true;
      if (suppressDesktopCloseTimerRef.current) {
        window.clearTimeout(suppressDesktopCloseTimerRef.current);
      }
      suppressDesktopCloseTimerRef.current = window.setTimeout(() => {
        suppressDesktopCloseRef.current = false;
        suppressDesktopCloseTimerRef.current = null;
      }, 0);
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const suppressDesktopClose = t2;
  let t3;
  if ($[7] !== isControlled || $[8] !== onOpenChange || $[9] !== openState) {
    t3 = nextOpenState => {
      const resolvedOpenState = typeof nextOpenState === "function" ? nextOpenState(openState) : nextOpenState;
      if (!enableClicks && !resolvedOpenState && suppressDesktopCloseRef.current) {
        return;
      }
      if (!isControlled) {
        setUncontrolledOpenState(resolvedOpenState);
      }
      onOpenChange?.(resolvedOpenState);
    };
    $[7] = isControlled;
    $[8] = onOpenChange;
    $[9] = openState;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const setIsOpen = t3;
  const t4 = !!openState;
  let t5;
  if ($[11] !== setIsOpen || $[12] !== t4) {
    t5 = {
      isOpen: t4,
      setIsOpen,
      triggerRef,
      suppressDesktopClose
    };
    $[11] = setIsOpen;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== children || $[15] !== t5) {
    t6 = <ToolTipTriggerProvider value={t5}>{children}</ToolTipTriggerProvider>;
    $[14] = children;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== openState || $[18] !== props || $[19] !== setIsOpen || $[20] !== t6) {
    t7 = <RdxTooltip.Root open={openState} onOpenChange={setIsOpen} {...props}>{t6}</RdxTooltip.Root>;
    $[17] = openState;
    $[18] = props;
    $[19] = setIsOpen;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
export type TooltipTriggerProps = RdxTooltip.TooltipTriggerProps;
export const TooltipTrigger = t0 => {
  const $ = _c(20);
  let children;
  let onClick;
  let onPointerDown;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      asChild: t1,
      onClick,
      onPointerDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClick;
    $[3] = onPointerDown;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    onClick = $[2];
    onPointerDown = $[3];
    props = $[4];
    t1 = $[5];
  }
  const asChild = t1 === undefined ? true : t1;
  const {
    setIsOpen,
    triggerRef,
    suppressDesktopClose
  } = useContext(TooltipTriggerContext);
  let t2;
  if ($[6] !== onClick || $[7] !== suppressDesktopClose) {
    t2 = e => {
      onClick?.(e);
      if (!enableClicks) {
        suppressDesktopClose();
        e.preventDefault();
        return;
      }
      if (enableClicks) {
        e.preventDefault();
      }
    };
    $[6] = onClick;
    $[7] = suppressDesktopClose;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== onPointerDown || $[10] !== setIsOpen || $[11] !== suppressDesktopClose) {
    t3 = e_0 => {
      onPointerDown?.(e_0);
      if (!enableClicks) {
        suppressDesktopClose();
        e_0.preventDefault();
        return;
      }
      if (e_0.defaultPrevented) {
        return;
      }
      e_0.preventDefault();
      setIsOpen(_temp);
    };
    $[9] = onPointerDown;
    $[10] = setIsOpen;
    $[11] = suppressDesktopClose;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== asChild || $[14] !== children || $[15] !== props || $[16] !== t2 || $[17] !== t3 || $[18] !== triggerRef) {
    t4 = <RdxTooltip.Trigger {...props} ref={triggerRef} asChild={asChild} className="Tooltip__trigger" onClick={t2} onPointerDown={t3}>{children}</RdxTooltip.Trigger>;
    $[13] = asChild;
    $[14] = children;
    $[15] = props;
    $[16] = t2;
    $[17] = t3;
    $[18] = triggerRef;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};
export interface TooltipContentProps extends RdxTooltip.TooltipContentProps {
  fitContent?: boolean;
  size?: 'sm' | 'md' | 'lg';
  usePortal?: boolean;
  defaultWrap?: boolean;
}
export const TooltipContent = t0 => {
  const $ = _c(35);
  let className;
  let fitContent;
  let props;
  let size;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let usePortal;
  if ($[0] !== t0) {
    ({
      align: t1,
      side: t2,
      size,
      alignOffset: t3,
      sideOffset: t4,
      fitContent,
      usePortal,
      defaultWrap: t5,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = fitContent;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = usePortal;
  } else {
    className = $[1];
    fitContent = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    usePortal = $[10];
  }
  const align = t1 === undefined ? "end" : t1;
  const side = t2 === undefined ? "top" : t2;
  const alignOffset = t3 === undefined ? -5 : t3;
  const sideOffset = t4 === undefined ? 5 : t4;
  const defaultWrap = t5 === undefined ? false : t5;
  const {
    triggerRef
  } = useContext(TooltipTriggerContext);
  let children;
  let contentProps;
  let onPointerDownOutside;
  if ($[11] !== props) {
    ({
      children,
      onPointerDownOutside,
      ...contentProps
    } = props);
    $[11] = props;
    $[12] = children;
    $[13] = contentProps;
    $[14] = onPointerDownOutside;
  } else {
    children = $[12];
    contentProps = $[13];
    onPointerDownOutside = $[14];
  }
  const t6 = size ? `Tooltip__content--${size}` : undefined;
  const t7 = fitContent && "Tooltip__content--fit";
  const t8 = !defaultWrap && "Tooltip__content--pretty";
  let t9;
  if ($[15] !== className || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = cn("Tooltip__content", className, t6, t7, t8);
    $[15] = className;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== onPointerDownOutside || $[21] !== triggerRef) {
    t10 = e => {
      onPointerDownOutside?.(e);
      if (e.defaultPrevented) {
        return;
      }
      const clickedTrigger = e.target instanceof Node && triggerRef.current?.contains(e.target);
      if (!enableClicks && clickedTrigger) {
        e.preventDefault();
      }
    };
    $[20] = onPointerDownOutside;
    $[21] = triggerRef;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <RdxTooltip.Arrow height={6} width={12} offset={20} className="Tooltip__arrow" />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== align || $[25] !== alignOffset || $[26] !== children || $[27] !== contentProps || $[28] !== side || $[29] !== sideOffset || $[30] !== t10 || $[31] !== t9) {
    t12 = <RdxTooltip.Content {...contentProps} align={align} side={side} alignOffset={alignOffset} sideOffset={sideOffset} className={t9} onPointerDownOutside={t10}>{children}{t11}</RdxTooltip.Content>;
    $[24] = align;
    $[25] = alignOffset;
    $[26] = children;
    $[27] = contentProps;
    $[28] = side;
    $[29] = sideOffset;
    $[30] = t10;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const tooltip = t12;
  if (usePortal) {
    let t13;
    if ($[33] !== tooltip) {
      t13 = <RdxTooltip.Portal>{tooltip}</RdxTooltip.Portal>;
      $[33] = tooltip;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    return t13;
  }
  return tooltip;
};
function _temp(currentIsOpen) {
  return !currentIsOpen;
}